// src/admin/pages/AdminTopBanner.js
import React, { useState, useEffect, useRef } from "react";
import AdminSidebar from "../AdminSidebar";
import AdminHeader from "../AdminHeader";
import { FaPlus, FaTrash, FaSave, FaCheck } from "react-icons/fa";
import {
  getAdminTopBanners,
  createAdminTopBanner,
  updateAdminTopBanner,
  deleteAdminTopBanner,
} from "../../services/adminTopBanner.api";
import { AdminLanguageContext } from "../context/AdminLanguageContext";
import { useContext } from "react";

function AdminTopBanner() {
  const { lang } = useContext(AdminLanguageContext);

  const [isOpen, setIsOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const [confirm, setConfirm] = useState(null); // { type, row }
  const [toast, setToast] = useState("");

  const newRowRef = useRef(null);
  const inputRef = useRef(null);
  
  const TEXT = {
  EN: {
    title: "Top Banner Messages",
    add: "Add",
    loading: "Loading…",
    placeholderEn: "English message",
    placeholderAr: "Arabic message",
    save: "Save",
    delete: "Delete",
    confirmSave: "Save changes?",
    confirmDelete: "Delete banner?",
    confirmText: "Are you sure you want to continue?",
    cancel: "Cancel",
    confirm: "Confirm",
    saved: "Banner saved successfully",
    deleted: "Banner deleted successfully",
  },
  AR: {
    title: "رسائل الشريط العلوي",
    add: "إضافة",
    loading: "جارٍ التحميل…",
    placeholderEn: "النص بالإنجليزية",
    placeholderAr: "النص بالعربية",
    save: "حفظ",
    delete: "حذف",
    confirmSave: "حفظ التعديلات؟",
    confirmDelete: "حذف الرسالة؟",
    confirmText: "هل أنت متأكد أنك تريد المتابعة؟",
    cancel: "إلغاء",
    confirm: "تأكيد",
    saved: "تم حفظ الرسالة بنجاح",
    deleted: "تم حذف الرسالة بنجاح",
  },
}[lang];

const adminUser = JSON.parse(localStorage.getItem("adminUser") || "{}");
const perms = JSON.parse(localStorage.getItem("staffPermissions") || {});
const isAdmin = adminUser.role === "admin";
const canAccess = isAdmin || perms.can_manage_banners;


  const load = () => {
    setLoading(true);
    getAdminTopBanners()
      .then(setRows)
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  /* AUTO HIDE TOAST */
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(""), 2000);
    return () => clearTimeout(t);
  }, [toast]);

  /* ADD */
  const handleAdd = async () => {
    const newRow = await createAdminTopBanner({
      message_en: "",
      message_ar: "",
      sort_order: rows.length + 1,
    });

    setRows((prev) => [...prev, newRow]);

    setTimeout(() => {
      newRowRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      inputRef.current?.focus();
    }, 100);
  };

  /* SAVE */
  const confirmSave = async (row) => {
    await updateAdminTopBanner(row.id, row);
    setToast(TEXT.saved);
    setConfirm(null);
    load();
  };

  /* DELETE */
  const confirmDelete = async (id) => {
    await deleteAdminTopBanner(id);
    setToast(TEXT.deleted);
    setConfirm(null);
    load();
  };

  return (
    <div className={`flex bg-gray-100 min-h-screen ${isOpen ? "overflow-hidden" : ""}`}>
      <AdminSidebar isOpen={isOpen} setIsOpen={setIsOpen} />
      <div className="flex-1 lg:ml-64">
        <AdminHeader setIsOpen={setIsOpen} />

        <main className="p-6">
  {!canAccess && (
    <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
      <p className="text-red-700 font-semibold">
        {lang === "AR"
          ? "ليس لديك صلاحية لإدارة البنرات"
          : "You do not have permission to manage banners."}
      </p>
    </div>
  )}

  {canAccess && (
    <>

          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold text-cyan-700">
               {TEXT.title}
            </h1>
            <button
              onClick={handleAdd}
              className="bg-cyan-600 text-white px-4 py-2 rounded-xl flex items-center gap-2"
            >
              <FaPlus /> {TEXT.add}
            </button>
          </div>

          {loading ? (
            <p>{TEXT.loading}</p>
          ) : (
            <div className="space-y-4">
              {rows.map((row, i) => {
                const isLast = i === rows.length - 1;

                return (
                  <div
                    key={row.id}
                    ref={isLast ? newRowRef : null}
                    className="bg-white border rounded-xl p-4 shadow space-y-2"
                  >
                    <input
                      ref={isLast ? inputRef : null}
                      className="w-full p-2 border rounded"
                      placeholder={TEXT.placeholderEn}
                      value={row.message_en}
                      onChange={(e) =>
                        setRows((r) =>
                          r.map((x) =>
                            x.id === row.id
                              ? { ...x, message_en: e.target.value }
                              : x
                          )
                        )
                      }
                    />

                    <input
                      className="w-full p-2 border rounded"
                      placeholder={TEXT.placeholderAr}
                      value={row.message_ar}
                      onChange={(e) =>
                        setRows((r) =>
                          r.map((x) =>
                            x.id === row.id
                              ? { ...x, message_ar: e.target.value }
                              : x
                          )
                        )
                      }
                    />

                    <div className="flex gap-2">
                      <button
                        onClick={() =>
                          setConfirm({ type: "save", row })
                        }
                        className="bg-green-600 text-white px-3 py-1 rounded flex items-center gap-1"
                      >
                        <FaSave /> {TEXT.save}
                      </button>

                      <button
                        onClick={() =>
                          setConfirm({ type: "delete", row })
                        }
                        className="bg-red-600 text-white px-3 py-1 rounded flex items-center gap-1"
                      >
                        <FaTrash /> {TEXT.delete}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
              </>
  )}

        </main>
      </div>

      {/* CONFIRM MODAL */}
      {confirm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-sm shadow-xl">
            <h2 className="text-lg font-bold mb-3">
              {confirm.type === "delete"
                ? TEXT.confirmDelete
                : TEXT.confirmSave}
            </h2>

            <p className="text-gray-600 mb-5">
              {TEXT.confirmText}
            </p>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setConfirm(null)}
                className="px-4 py-2 border rounded-xl"
              >
                {TEXT.cancel}
              </button>

              <button
                onClick={() =>
                  confirm.type === "delete"
                    ? confirmDelete(confirm.row.id)
                    : confirmSave(confirm.row)
                }
                className={`px-4 py-2 rounded-xl text-white ${
                  confirm.type === "delete"
                    ? "bg-red-600"
                    : "bg-green-600"
                }`}
              >
                {TEXT.confirm}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* TOAST */}
      {toast && (
        <div className="fixed bottom-6 right-6 bg-green-600 text-white px-5 py-3 rounded-xl shadow-lg flex items-center gap-2 z-50">
          <FaCheck /> {toast}
        </div>
      )}
    </div>
  );
}

export default AdminTopBanner;

